/**
 * DSL id-resolution with strict stale/hallucinated-ID detection.
 *
 * Kept in its own module (no bpmn.js / designer-action imports) so tests can
 * exercise the ref-validation logic without pulling in the heavy browser
 * bring-up chain used by workflow-dsl-executor.
 */

/** Records a PATH that was already created from a gateway to a target in this script. */
export interface PathEdgeRecord {
  pathId: string;
  pathName?: string;
  line: number;
  /**
   * KOA-18136 §35 — was this edge ALREADY on the diagram when the script started
   * (seeded from `LIST`), or did THIS script create it?
   *
   * The two cases need opposite answers. A second `PATH` to the same target in
   * the SAME script is a real conflict: bpmn.js merges them and one condition is
   * silently lost. But an edge that already existed is the normal shape of a
   * recovery script — the author is re-stating the branch to give it the
   * condition or default marker it is missing. Refusing that is what left four
   * gateways condition-less on the live run (session d36d78e2) and sent the
   * builder into 21 turns of moving the default flag back and forth.
   */
  preexisting: boolean;
}

/** Script-wide execution context — tracks IDs the LLM is allowed to reference. */
export interface ExecCtx {
  /** alias → real BPMN id map, mutated during script execution. */
  aliasMap: Record<string, string>;
  /** Real BPMN IDs known to exist (LIST output + creates + well-known anchors). */
  verifiedIds: Set<string>;
  /** True once LIST ran in this script. Gates stale-ID validation. */
  hasListed: boolean;
  /** `${sourceId}->${targetId}` → existing PATH record. Used to reject duplicate-target PATHs. */
  pathEdges: Map<string, PathEdgeRecord>;
}

/** Canonical key for a (gateway, target) path edge. */
export function pathEdgeKey(sourceId: string, targetId: string): string {
  return `${sourceId}->${targetId}`;
}

/**
 * Resolve a DSL ref to a real BPMN id.
 *
 * - `@alias` → aliasMap lookup; throws if unresolved (always, regardless of ctx).
 * - literal BPMN id → returned as-is, but when `ctx.hasListed` is true and the
 *   id is not in `ctx.verifiedIds`, throws to catch IDs pasted from a prior
 *   conversation. When LIST hasn't run we stay lenient — action functions
 *   further downstream will return "Element not found" for bad ids.
 */
export function resolve(ref: string, aliasMap: Record<string, string>, ctx?: ExecCtx): string {
  if (ref.startsWith("@")) {
    const real = aliasMap[ref];
    if (!real) throw new Error(`Unresolved alias: ${ref}`);
    return real;
  }
  const mapped = aliasMap[ref] || ref;
  if (ctx && ctx.hasListed && !ctx.verifiedIds.has(ref) && !ctx.verifiedIds.has(mapped)) {
    throw new Error(
      `Stale or hallucinated ID "${ref}": not in this script's LIST output. ` +
        `IDs from a previous conversation are not valid. Use ONLY IDs that appear in the fresh LIST output ` +
        `(look at the \`steps[*].id\` field). Never paste IDs from prior scripts.`
    );
  }
  return mapped;
}
